import { useLenis } from 'lenis/react'
import { useEffect, useRef, type MouseEvent, type RefObject } from 'react'
import { Button, PrismLight } from '@/components/ui'
import { nav, site } from '@/content/site'
import { cx } from '@/lib/cx'
import { EASE, gsap, useGSAP } from '@/lib/gsap'
import { useAppStore } from '@/lib/store'
import styles from './Menu.module.css'

/** Props for {@link Menu}. */
export interface MenuProps {
  open: boolean
  /** The header holding the bar and this menu: the focus trap spans both. */
  headerRef: RefObject<HTMLElement | null>
  /** The toggle: the menu opens from it and returns focus to it. */
  toggleRef: RefObject<HTMLButtonElement | null>
  onNavigate: (event: MouseEvent<HTMLAnchorElement>, href: string) => void
  /** Escape was pressed. */
  onRequestClose: () => void
}

const FOCUSABLE = 'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'

function isShown(element: HTMLElement): boolean {
  return (
    element.getClientRects().length > 0 &&
    !element.closest('[hidden]') &&
    getComputedStyle(element).visibility !== 'hidden'
  )
}

/**
 * The small-screen menu: pearl glass over soft prism light, with compositor-only fades.
 * Disclosure pattern: the toggle carries `aria-expanded` /
 * `aria-controls`; Tab is trapped in the header while open; Escape closes it.
 * Scroll is locked behind it; the list itself can scroll on short screens.
 */
export function Menu({ open, headerRef, toggleRef, onNavigate, onRequestClose }: MenuProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const timelineRef = useRef<gsap.core.Timeline | null>(null)
  const shownRef = useRef(false)
  const lenis = useLenis()
  const lenisRef = useRef(lenis)
  lenisRef.current = lenis
  const closeRef = useRef(onRequestClose)
  closeRef.current = onRequestClose

  useGSAP(
    () => {
      const root = rootRef.current
      if (!root) return
      if (!open && !shownRef.current) {
        root.hidden = true
        return
      }

      const q = gsap.utils.selector(root)
      const labels = q('[data-menu-label]')
      const fades = q('[data-menu-fade]')
      const reduced = useAppStore.getState().reducedMotion
      timelineRef.current?.kill()

      if (open) {
        const wasHidden = root.hidden
        shownRef.current = true
        root.hidden = false
        root.inert = false
        lenisRef.current?.stop()
        if (wasHidden) gsap.set(root, { autoAlpha: 0, y: reduced ? 0 : -8 })
        gsap.set(fades, { autoAlpha: 1, y: 0 })
        timelineRef.current = gsap
          .timeline()
          .to(root, { autoAlpha: 1, y: 0, duration: reduced ? 0.25 : 0.45, ease: EASE.out }, 0)
          .fromTo(
            labels,
            { yPercent: reduced ? 0 : 22, opacity: 0 },
            {
              yPercent: 0,
              opacity: 1,
              duration: reduced ? 0.25 : 0.45,
              stagger: 0.035,
              ease: EASE.out,
            },
            0.04,
          )
          .call(
            () =>
              root.querySelector<HTMLElement>('[data-menu-link]')?.focus({ preventScroll: true }),
            undefined,
            0.08,
          )
        return
      }

      // Closing.
      if (root.contains(document.activeElement)) toggleRef.current?.focus({ preventScroll: true })
      root.inert = true
      const finish = () => {
        root.hidden = true
        shownRef.current = false
        if (useAppStore.getState().phase !== 'boot') lenisRef.current?.start()
      }
      timelineRef.current = gsap.timeline({ onComplete: finish }).to(root, {
        autoAlpha: 0,
        y: reduced ? 0 : -6,
        duration: reduced ? 0.2 : 0.3,
        ease: EASE.inOut,
      })
    },
    { scope: rootRef, dependencies: [open] },
  )

  // Focus trap across the header (bar + menu) and Escape to close.
  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        closeRef.current()
        return
      }
      const header = headerRef.current
      if (event.key !== 'Tab' || !header) return
      const items = Array.from(header.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(isShown)
      if (items.length === 0) return
      const first = items[0]
      const last = items[items.length - 1]
      const active = document.activeElement as HTMLElement | null
      if (!active || !items.includes(active)) {
        event.preventDefault()
        ;(event.shiftKey ? last : first).focus()
      } else if (event.shiftKey && active === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, headerRef])

  // Unmounting while open (crossing to desktop): give the page its scroll back.
  useEffect(
    () => () => {
      if (shownRef.current && useAppStore.getState().phase !== 'boot') lenisRef.current?.start()
    },
    [],
  )

  return (
    <div ref={rootRef} id="site-menu" className={styles.menu} data-lenis-prevent>
      <div className={cx(styles.layer, styles.glass)} data-menu-layer />
      <div className={cx(styles.layer, styles.light)} data-menu-layer>
        <PrismLight placement="top-right" intensity={0.65} />
      </div>

      <div className={styles.body} data-menu-body>
        <nav className={styles.nav} aria-label={nav.ui.label}>
          <ol className={styles.list} role="list">
            {nav.links.map((link, index) => (
              <li key={link.href}>
                <a
                  className={styles.link}
                  href={link.href}
                  data-menu-link
                  data-nav-target={link.href.slice(1)}
                  onClick={(event) => onNavigate(event, link.href)}
                >
                  <span className={styles.index} aria-hidden="true" data-menu-fade>
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className={styles.labelMask}>
                    <span className={styles.label} data-menu-label>
                      {link.label}
                    </span>
                  </span>
                  <span className={styles.current} aria-hidden="true" />
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className={styles.foot}>
          <span data-menu-fade>
            <Button href={nav.cta.href} external icon="arrow-up-right" size="lg">
              {nav.cta.label}
            </Button>
          </span>
          <p className={styles.tagline} data-menu-fade>
            {site.tagline}
          </p>
        </div>
      </div>
    </div>
  )
}
